import { Clickable } from "@client/components/Clickables";
import React from "react";

import { SimpleLinkBlock } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import ContactUsForm from "../../../ContactUsForm";

import styles from "./index.module.scss";

export { styles as ctaStyles };

type Target =
    | { link: SimpleLinkBlock; opensContactModal?: never }
    | { link?: never; opensContactModal: true };

export type CtaProps = Target & {
    /** `dark`: solid on light grounds. `outlineOnDark`: the footer's. */
    variant: "dark" | "outlineOnDark";
    /** The label, set twice for the hover roll. */
    children: string;
    className?: string;
};

/** The label twice, stacked in a one-line clip, for the hover roll */
export const CtaLabel = (props: { children: string }) => (
    <span className={styles.clip}>
        <span className={styles.roll}>
            <span>{props.children}</span>
            <span aria-hidden="true">{props.children}</span>
        </span>
    </span>
);

/**
 * The 2026 CTA pill. Renders a link, or a button that opens the contact
 * modal. Wrap it in `<Mask cta>` to reveal it.
 */
export const Cta = (props: CtaProps) => {
    const className = concatClassNames([
        styles[props.variant],
        props.className,
    ]);
    const label = <CtaLabel>{props.children}</CtaLabel>;

    if (props.opensContactModal) {
        return (
            // The repo's Clickable: react-aria's useButton is what makes it a
            // pressable child for the modal's DialogTrigger. The header's
            // instance owns the page's contact hooks
            <ContactUsForm hooks={false}>
                <Clickable className={className}>{label}</Clickable>
            </ContactUsForm>
        );
    }

    // A deleted or unpublished target page leaves no URL: render nothing
    // rather than a pill that reloads the page. The URL already carries the
    // anchor (thelabui appends it server side)
    if (props.link.url === null) {
        return null;
    }
    const external = props.link.link_type === "external";
    return (
        <Clickable
            className={className}
            href={props.link.url}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener" : undefined}
        >
            {label}
        </Clickable>
    );
};
